import { useEffect, useState } from 'react'
import { Navigate, Route, Routes, useNavigate } from 'react-router-dom'
import type { User } from 'oidc-client-ts'
import { completeSignIn, currentUser, isAdmin, signIn, signOut, userManager } from './lib/auth'
import { AppLayout } from './components/AppLayout'
import { DashboardPage } from './pages/DashboardPage'
import { UsersPage } from './pages/UsersPage'
import { UserDetailPage } from './pages/UserDetailPage'
import { JobsPage } from './pages/JobsPage'
import { CostsPage } from './pages/CostsPage'
import { CatalogPage } from './pages/CatalogPage'
import { DiscoverPage } from './pages/DiscoverPage'
import { AuditPage } from './pages/AuditPage'
import { Button } from './components/ui/button'
import { ShieldAlert, Sparkles } from 'lucide-react'

type AuthState =
  | { status: 'loading' }
  | { status: 'signed-out' }
  | { status: 'forbidden'; user: User }
  | { status: 'ready'; user: User }

export function App() {
  const [state, setState] = useState<AuthState>({ status: 'loading' })

  useEffect(() => {
    let cancelled = false

    async function resolve() {
      const user = await currentUser()
      if (cancelled) return
      if (!user) {
        setState({ status: 'signed-out' })
        return
      }
      // The panel is hidden from non-admins as a courtesy; the API is what
      // actually refuses their requests.
      setState(isAdmin(user) ? { status: 'ready', user } : { status: 'forbidden', user })
    }

    void resolve()

    // A silent renew swaps the token underneath us; re-evaluating keeps the
    // role check current if it is ever revoked mid-session.
    const onUserLoaded = () => void resolve()
    const onUserUnloaded = () => setState({ status: 'signed-out' })
    userManager.events.addUserLoaded(onUserLoaded)
    userManager.events.addUserUnloaded(onUserUnloaded)

    return () => {
      cancelled = true
      userManager.events.removeUserLoaded(onUserLoaded)
      userManager.events.removeUserUnloaded(onUserUnloaded)
    }
  }, [])

  return (
    <Routes>
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route
        path="/*"
        element={
          state.status === 'loading' ? (
            <CentredMessage title="Loading" />
          ) : state.status === 'signed-out' ? (
            <SignInScreen />
          ) : state.status === 'forbidden' ? (
            <ForbiddenScreen />
          ) : (
            <AppLayout user={state.user}>
              <Routes>
                <Route path="/" element={<DashboardPage />} />
                <Route path="/users" element={<UsersPage />} />
                <Route path="/users/:userId" element={<UserDetailPage />} />
                <Route path="/jobs" element={<JobsPage />} />
                <Route path="/costs" element={<CostsPage />} />
                <Route path="/catalog" element={<CatalogPage />} />
                <Route path="/discover" element={<DiscoverPage />} />
                <Route path="/audit" element={<AuditPage />} />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </AppLayout>
          )
        }
      />
    </Routes>
  )
}

function AuthCallback() {
  const navigate = useNavigate()
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    completeSignIn()
      .then(() => navigate('/', { replace: true }))
      .catch((e: unknown) => setError(e instanceof Error ? e.message : 'Sign-in failed'))
  }, [navigate])

  return error ? (
    <CentredMessage title="Sign-in failed" body={error} action={{ label: 'Try again', onClick: () => void signIn() }} />
  ) : (
    <CentredMessage title="Signing you in" />
  )
}

function SignInScreen() {
  return (
    <CentredMessage
      icon={<Sparkles className="size-8 text-[var(--color-accent-soft)]" />}
      title="Pixviro Admin"
      body="Sign in with your Pixviro administrator account."
      action={{ label: 'Sign in with Keycloak', onClick: () => void signIn() }}
    />
  )
}

function ForbiddenScreen() {
  return (
    <CentredMessage
      icon={<ShieldAlert className="size-8 text-[var(--color-warning)]" />}
      title="Not an administrator"
      body="This account is signed in but does not hold the Pixviro admin role. Ask a realm administrator to grant it."
      action={{ label: 'Sign out', onClick: () => void signOut() }}
    />
  )
}

function CentredMessage({
  icon,
  title,
  body,
  action,
}: {
  icon?: React.ReactNode
  title: string
  body?: string
  action?: { label: string; onClick: () => void }
}) {
  return (
    <div className="flex min-h-screen items-center justify-center p-6">
      <div className="w-full max-w-sm space-y-4 text-center">
        {icon && <div className="flex justify-center">{icon}</div>}
        <h1 className="text-xl font-semibold">{title}</h1>
        {body && <p className="text-sm text-[var(--color-text-secondary)]">{body}</p>}
        {action && (
          <Button className="w-full" onClick={action.onClick}>
            {action.label}
          </Button>
        )}
      </div>
    </div>
  )
}
